import { LoadingButton } from '@mui/lab'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { cancelRegistration } from 'api/auth'
import NotificationSys from 'components/NotificationSystem'
import { useState } from 'react'

/**
 * Deletes the account left behind by a declined payment so the same email can register again.
 * The password is asked for rather than carried over from the form: this deletes an account, and
 * the user is not signed in, so the backend has nothing else to authorize the request with.
 */
export function CancelRegistrationModal({
  isOpen,
  onClose,
  onDeleted,
}: {
  isOpen: boolean
  onClose: () => void
  onDeleted: () => void
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)

  const canSubmit = email.trim().length > 0 && password.length > 0

  const handleConfirm = async () => {
    try {
      setLoading(true)
      await cancelRegistration(email.trim(), password)
      NotificationSys.showSuccess('Registration cancelled. You can sign up again with this email.')
      onDeleted()
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Cancel registration</DialogTitle>

      <DialogContent>
        <Stack spacing={2} pt={1}>
          <Typography variant="body2" color="text.secondary">
            This deletes the account you just created, so you can start the registration over with
            the same email. Confirm with the credentials you signed up with.
          </Typography>

          <TextField
            label="Email"
            type="email"
            size="small"
            fullWidth
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />

          <TextField
            label="Password"
            type="password"
            size="small"
            fullWidth
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} color="info">
          Keep my account
        </Button>
        <LoadingButton
          variant="contained"
          color="error"
          onClick={handleConfirm}
          loading={loading}
          disabled={!canSubmit}
        >
          Delete account
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
